import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Grid,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { SportsSoccer as GoalIcon } from '@mui/icons-material';
import TeamLogo from '../team/TeamLogo';
import unifiedApi from '../../utils/unifiedApi';
import { useAuth } from '../../contexts/AuthContext';

const LeadingScorerCard = ({ scorer, maxGoals, actualPosition, showPosition }) => {
  const percentage = maxGoals > 0 ? (scorer.goalsScored / maxGoals) * 100 : 0;
  const isTopScorer = actualPosition === 1;

  return (
    <Card 
      elevation={isTopScorer ? 4 : 2}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        border: isTopScorer ? '2px solid #FFD700' : 'none',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isTopScorer ? '0 8px 20px rgba(255,215,0,0.3)' : '0 6px 16px rgba(0,0,0,0.1)',
        },
      }}
    >
      {isTopScorer && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1
          }}
        >
          <Chip
            icon={<GoalIcon />}
            label="Top Scorer"
            size="small"
            sx={{
              backgroundColor: '#FFD700',
              color: '#000',
              fontWeight: 'bold',
              fontSize: '0.7rem'
            }}
          />
        </Box>
      )}
      
      <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Team Logo - Full Width Background */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            mb: 2,
            minHeight: 80,
            position: 'relative'
          }}
        >
          {/* Position Badge - Only show if showPosition is true */}
          {showPosition && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: isTopScorer ? '#FFD700' : 'primary.main',
                color: isTopScorer ? '#000' : 'white',
                borderRadius: '50%',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                zIndex: 1
              }}
            >
              {actualPosition}
            </Box>
          )}
          
          {/* Team Logo or Spare Badge */}
          {scorer.isSpare ? (
            <Box
              sx={{
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'warning.main',
                borderRadius: '50%',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}
            >
              SPARE
            </Box>
          ) : (
            <TeamLogo 
              team={{ logoUrl: scorer.logoUrl, name: scorer.teamName }}
              size={80}
              sx={{ flexShrink: 0 }}
            />
          )}
        </Box>

        {/* Player Name */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: '1rem',
            fontWeight: 'bold',
            lineHeight: 1.2,
            mb: 1,
            textAlign: 'center'
          }}
        >
          {scorer.fullName}
        </Typography>

        {/* Team Name */}
        <Typography 
          variant="body2" 
          color={scorer.isSpare ? "warning.main" : "text.secondary"}
          sx={{ 
            fontSize: '0.8rem',
            textAlign: 'center',
            mb: 2,
            fontWeight: scorer.isSpare ? 'bold' : 'normal'
          }}
        >
          {scorer.teamName}
        </Typography>

        {/* Goals Section */}
        <Box sx={{ mt: 'auto' }}>
          {/* Goal Count */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <GoalIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                color: isTopScorer ? '#FFD700' : 'primary.main'
              }}
            >
              {scorer.goalsScored}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              goal{scorer.goalsScored !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: isTopScorer ? '#FFD700' : 'primary.main',
                  borderRadius: 4,
                },
              }}
            />
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                textAlign: 'center', 
                mt: 0.5,
                fontSize: '0.7rem'
              }}
            >              
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const LeadingScorers = ({ 
  leagueSlug = null, 
  leagueId = null, 
  limit = 10, 
  minimumGoals = 0,
  title = "Leading Scorers",
  isPublic = true 
}) => {
  const [scorers, setScorers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const _mode = user ? user.role : 'public';

  useEffect(() => {
    const loadLeadingScorers = async () => {
      try {
        setLoading(true);
        setError(null);

        let data;
        if (isPublic && leagueSlug) {
          // Use public API with league slug
          data = await unifiedApi.getLeadingScorers(leagueSlug, { limit, minimumGoals });
        } else if (!isPublic && leagueId) {
          // TODO: There's no leagueId-based leading scorers endpoint; callers must pass leagueSlug
          // (GET /api/leagues/{key}/leading-scorers).
          throw new Error('Authenticated API not yet implemented');
        } else {
          throw new Error('Either leagueSlug (for public) or leagueId (for authenticated) must be provided');
        }

        if (!data) {
          setError('No scoring data available for this league');
          return;
        }

        if (!data.scorers || data.scorers.length === 0) {
          setScorers([]);
          return;
        }

        setScorers(data.scorers);
      } catch (err) {
        const errorMessage = err.message || 'Failed to load leading scorers';
        setError(errorMessage);
        console.error('Error loading leading scorers:', err);
      } finally {
        setLoading(false);
      }
    };

    if ((isPublic && leagueSlug) || (!isPublic && leagueId)) {
      loadLeadingScorers();
    } else {
      setLoading(false);
      setError('Missing required parameters');
    }
  }, [leagueSlug, leagueId, limit, minimumGoals, isPublic]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!scorers || scorers.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        No goal scorers found for this league yet.
      </Alert>
    );
  }

  const maxGoals = scorers.length > 0 ? scorers[0].goalsScored : 1;

  // Calculate positions accounting for ties
  const scorersWithPositions = [];
  let currentPosition = 1;
  
  for (let i = 0; i < scorers.length; i++) {
    const scorer = scorers[i];
    
    // If this isn't the first scorer and they have fewer goals than the previous,
    // update the position to be the current index + 1
    if (i > 0 && scorer.goalsScored < scorers[i - 1].goalsScored) {
      currentPosition = i + 1;
    }
    
    // Check if this is the first occurrence of this goal count
    const isFirstWithThisScore = i === 0 || scorers[i - 1].goalsScored !== scorer.goalsScored;
    
    scorersWithPositions.push({
      ...scorer,
      actualPosition: currentPosition,
      showPosition: isFirstWithThisScore
    });
  }

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 'bold',
            mb: 1
          }}
        >
          <GoalIcon color="primary" />
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Top {scorers.length} goal scorers in the league
        </Typography>
      </Box>

      {/* Scorers Grid - Max 5 per row */}
      <Grid container spacing={2}>
        {scorersWithPositions.map((scorer) => (
          <Grid 
            size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}
            key={`${scorer.userTenantId}-${scorer.teamId}`}
          >
            <LeadingScorerCard 
              scorer={scorer} 
              maxGoals={maxGoals} 
              actualPosition={scorer.actualPosition}
              showPosition={scorer.showPosition}
            />
          </Grid>
        ))}
      </Grid>

      {/* Show message if fewer than requested limit */}
      {scorers.length < limit && scorers.length > 0 && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ textAlign: 'center', mt: 2 }}
        >
          Showing all {scorers.length} scorer{scorers.length !== 1 ? 's' : ''} in the league
        </Typography>
      )}
    </Box>
  );
};

export default LeadingScorers;